/**
 * Regular expression to match spoiler tags and their content
 * - Uses non-greedy quantifier (.*?) to match minimal content between tags
 * - 's' flag allows . to match newline characters
 */
const SPOILER_REGEX = /\[spoiler](.*?)\[\/spoiler]/gs;

/**
 * Safely truncates text while preserving spoiler tag integrity
 * @param text - Input text to process (can be null/undefined)
 * @param limit - Maximum allowed length
 * @returns Truncated text with complete spoiler tags
 */
export const cutSpoilers = (
  text: string | undefined | null,
  limit: number
): string => {
  if (!text) return '';
  if (text.length <= limit) return text;

  const sliced = text.slice(0, limit);

  const incompleteTagMatch = sliced.match(/\[s?p?o?i?l?e?r?$/);

  if (incompleteTagMatch) {
    return sliced.slice(0, sliced.lastIndexOf(incompleteTagMatch[0]));
  }

  const spoilerTags = Array.from(
    sliced.matchAll(/\[spoiler]|\[\/spoiler]/g) || []
  );
  const openTags = spoilerTags.filter(tag => tag[0] === '[spoiler]').length;
  const closeTags = spoilerTags.filter(tag => tag[0] === '[/spoiler]').length;

  if (openTags === closeTags) return sliced;

  const lastOpenIndex = sliced.lastIndexOf('[spoiler]');
  return lastOpenIndex >= 0 ? sliced.slice(0, lastOpenIndex) : sliced;
};

/**
 * Extracts spoiler content and normal text segments
 * @param text - Input text containing spoilers
 * @returns Object with:
 *   textParts: Array of non-spoiler text segments
 *   spoilerParts: Array of spoiler contents
 */
export const extractSpoilers = (
  text: string
): { textParts: string[]; spoilerParts: string[] } => {
  const parts: string[] = [];
  const spoilers: string[] = [];

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = SPOILER_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    spoilers.push(match[1]);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return {
    textParts: parts.length > 0 ? parts : [text],
    spoilerParts: spoilers
  };
};
